import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AlertListService } from '@xref/ngx';

import { PermissionsService } from '../../services/permissions/permissions.service';
import { TokenService } from '../../services/token/token.service';
import { MFA_FEATURE_FLAG, TwoFactorService } from '../../services/two-factor/two-factor.service';
import Constants from '../../shared/constants';

@Component({
    selector: 'xlog-two-factor',
    templateUrl: './two-factor.component.pug',
    styleUrls: ['./two-factor.component.scss'],
    standalone: false
})
export class TwoFactorComponent implements OnInit {
    @Input() verificationType: any;

    loaded = false;
    isSetup: boolean;

    constructor(
        private router: Router,
        private token: TokenService,
        public twoFactor: TwoFactorService,
        private alertList: AlertListService,
        private jwtHelper: JwtHelperService,
        private permissions: PermissionsService,
    ) {}

    ngOnInit() {
        this.markSignupMfaInProgressFromToken();
        this.getTwoFactorConfig();
    }

    /**
   * Set isSetup to true or false. Used to force user between the setup and verification forms.
   * @param {boolean} isSetup
   */
    setupComplete(isSetup: boolean) {
        this.isSetup = isSetup;
    }

    /**
   * Get 2FA config for the specific user.
   */
    private getTwoFactorConfig() {
        const success = () => {
            // Dirty hack for legacy (Recruiter/Admin) users with ghosting enabled.
            // If both email and mobile options are disabled force both to be
            // available since config is missing in these cases. Enterprise (XSP)
            // overviews carry their own acceptance flags and are left untouched.
            if (!this.twoFactor.isEnterprise()
                && !this.isXspMfaSetupToken()
                && !this.twoFactor.data.email_allowed
                && !this.twoFactor.data.mobile_allowed) {
                this.twoFactor.data.email_allowed = true;
                this.twoFactor.data.mobile_allowed = true;
            }

            this.loaded = true;
            this.setupComplete(this.twoFactor.data.mfa_enabled);
        };

        const error = (err: any) => {
            if (err.error.hasOwnProperty('detail')) {
                this.alertList.append([{active: true, type: 'danger', messageKey: `alerts.auth.${err.error.detail}`, keepOnRouteChange: true}]);
            } else {
                this.alertList.append([{active: true, type: 'danger', messageKey: 'alerts.generic.error', keepOnRouteChange: true}]);
            }

            this.router.navigate(['/']);
        };

        this.twoFactor.getConfig(this.token.data.token, success, error, true);
    }

    private markSignupMfaInProgressFromToken(): void {
        if (!this.permissions.isFeatureActivated(MFA_FEATURE_FLAG)) {
            return;
        }
        if (this.decodeSetupToken()?.signup_mfa) {
            sessionStorage.setItem(Constants.SIGNUP_MFA_IN_PROGRESS, 'true');
        }
    }

    private isXspMfaSetupToken(): boolean {
        return !!this.decodeSetupToken()?.xsp_mfa;
    }

    /**
     * A malformed token cannot normally reach here (TokenGuardService validates it)
     * and getTwoFactorConfig surfaces the failure, so decoding stays non-fatal.
     */
    private decodeSetupToken(): any {
        try {
            return this.jwtHelper.decodeToken(this.token.data.token);
        } catch {
            return null;
        }
    }

}
